// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";
import { Button } from "react-bootstrap";
import { DateTime } from "luxon";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { formatTimestamp, isSet } from "shared/utils/utils";
import {
  Report,
  TrendReportContent,
  Period,
  extractIOBusModuleChannel,
  ICloudParameter,
  IVirtAssign,
} from "shared/types/cloudtypes";
import { IAction } from "actions";
import { getBusNr, IInput, IOutput, ITariffCircuit } from "shared/types/parameter";
import { IState } from "states/states";
import { IReportOptions } from "states/guistates";
import { getEnergyUnit } from "guiutils";

// !!!! any changes here should
// also get updated in reportdata.ts!!!!!
export enum SpecUnit {
  DATA = 1,
  DATAFLOW = 2,
  TARIFFLINE = 3,
  TIMESTAMP = 4,
  INTERVAL = 5,
  IO = 6,
}

interface IReportDataColumn {
  title: string;
  unit?: string | SpecUnit;
  specialDataCol?: string;
  decimals?: number;
  aggregation?: "SUM" | "MAX" | "AVERAGE";
}

interface IReportDataPayload {
  colums: IReportDataColumn[];
  data: any[];
  error: string[];
}

interface ISumData {
  value: number;
  unit: string;
  avgCnt: number;
}

interface IReportDataViewExtProps {}

type IReportDataViewProps = IReportOptions & {
  reportData: IReportDataPayload | null;
  report: Report | undefined;
  inputs: IInput[] | undefined;
  outputs: IOutput[] | undefined;
  tariffLines: ITariffCircuit[] | undefined;
  reportError: string | undefined;
  siteId: number | undefined;
  virtualTariffLines: IVirtAssign[] | undefined;
};

const mapStateToProps = (state: IState, ownProps: IReportDataViewExtProps) => {
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);
  if (site === undefined) {
    console.error("admin unidentified site", siteId, state.site.siteList);
  }

  const props: IReportDataViewProps = {
    ...state.gui.reportOptions,
    report: state.site.reportList?.find((curItem) => curItem.id === state.router.report),
    reportData: (state.gui.reportData?.data as IReportDataPayload) ?? null,
    reportError: state.gui.reportData?.error,
    inputs: state.para.parameter?.input,
    outputs: state.para.parameter?.output,
    tariffLines: state.para.parameter?.tariffline,
    siteId: state.cached.selectedSite,
    virtualTariffLines: (state.para.parameter as ICloudParameter | undefined)?.virtualSite
      .tariffline,
  };
  return props;
};

interface IReportDataViewDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IReportDataViewDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({});

class ReportDataViewComponent extends React.Component<
  IReportDataViewProps & IReportDataViewExtProps & IReportDataViewDispatch & RoutedProps
> {
  private createTable() {
    const columns: JSX.Element[] = [];

    this.props.reportData?.colums?.forEach((column, index) => {
      columns.push(
        <th
          key={`headerCol_${index}`}
          className={`${column.decimals !== undefined ? "right" : ""}`}
        >
          {column.title}
        </th>,
      );
    });

    return (
      <div className="datatable_container html-report">
        <table className="datatable">
          <thead>
            <tr>{columns}</tr>
          </thead>
          <tbody>{this.createListEntries()}</tbody>
        </table>
      </div>
    );
  }

  private updateSumRowData(
    sumRow: ISumData[],
    index: number,
    value: number,
    aggregation: string | undefined,
  ) {
    // update sum-row data
    // we need to make a sumRow entry even if we do not need a sum for this column
    if (!isSet(sumRow[index])) {
      sumRow[index] = { value: 0, avgCnt: 0, unit: "" };
    }
    switch (aggregation) {
      case "AVERAGE":
        sumRow[index].value += Number(value);
        sumRow[index].avgCnt++;
        break;
      case "SUM":
        sumRow[index].value += Number(value);
        break;
      case "MAX":
        sumRow[index].value = Math.max(sumRow[index].value, Number(value));
        break;
    }
  }

  // !!!! any changes here (also in methods called by this method) should
  // also get updated in reportexport.ts!!!!!
  private createListEntries() {
    const anArray: JSX.Element[] = [];
    const sumRow: ISumData[] = [];

    // we need to loop thru the columns first -> so we can decode tariffline cells and get the unit
    // which should be used on several other columns with DATA or DATAFLOW unit
    let tariffLine: ITariffCircuit | undefined;

    // loop thru all data-rows...
    this.props.reportData?.data?.forEach((entry, indexRow) => {
      const aColumnArray: JSX.Element[] = [];

      // get the index of the tariffline column
      const tariffLineColumnIdx =
        this.props.reportData?.colums?.findIndex((column) => column.unit === SpecUnit.TARIFFLINE) ??
        -1;

      // if we found a tariffline column, decode the tariffline for this row
      if (tariffLineColumnIdx !== -1) {
        const value: string | number | Date | undefined = entry[tariffLineColumnIdx];
        // tariff circuit cell; decode value
        const siteId = ((value as number) >> 16) & 0xffff;
        const tlId: number = (value as number) & 0xffff;

        // virtual site TL?
        if (siteId === this.props.siteId) {
          tariffLine = this.props.tariffLines?.find(
            (curItem, curIndex) => (curItem.id ?? curIndex) === tlId,
          );
        } else {
          // virtual site TL; get destination TL index
          const destIndex = this.props.virtualTariffLines?.find(
            (curItem) => curItem.sourceSite === siteId && curItem.sourceId === tlId,
          )?.destIndex;
          if (isSet(destIndex)) {
            tariffLine = this.props.tariffLines?.[destIndex];
          }
        }
        // sanity check if tariff line is valid/found
        console.assert(isSet(tariffLine), "invalid tariff line", value, siteId, tlId);
      }

      // now loop thru all columns and prepare data with the known tariffLine (and units for it)
      this.props.reportData?.colums?.forEach((column, indexCol) => {
        let value: string | number | Date | undefined = entry[indexCol];
        let columnUnit: string | undefined;
        // we need to update the sum row before data processing to avoid replacing durations with strings
        this.updateSumRowData(sumRow, indexCol, value as number, column.aggregation);
        if (isSet(column.unit)) {
          if (typeof column.unit === "number") {
            switch (column.unit) {
              case SpecUnit.DATA:
                columnUnit = getEnergyUnit(tariffLine?.unit, false);
                break;
              case SpecUnit.DATAFLOW:
                columnUnit = tariffLine?.unit;
                break;
              case SpecUnit.TIMESTAMP:
                {
                  // set time format according aggregation; this is used in TrendReports and IoReports
                  const trendingContent = this.props.report?.content as
                    TrendReportContent | undefined;
                  let dateFmt: string;

                  const aggregation =
                    trendingContent?.groupBy?.includes("date") ?
                      trendingContent?.aggregation
                    : undefined;

                  if (aggregation === Period.HOUR) {
                    dateFmt = "dd.MM.yyyy HH:mm";
                  } else if (aggregation === Period.DAY) {
                    dateFmt = "dd.MM.yyyy";
                  } else if (aggregation === Period.MONTH) {
                    dateFmt = "MMM yyyy";
                  } else if (aggregation === Period.YEAR) {
                    dateFmt = "yyyy";
                  } else {
                    // trendingContent?.aggregation === "none" or undefined
                    dateFmt = "dd.MM.yyyy HH:mm";
                  }
                  // also set formatted string, which is used for csv like exports
                  value = isSet(value) ? DateTime.fromISO(value as string).toFormat(dateFmt) : "";
                }
                break;
              case SpecUnit.TARIFFLINE:
                // tariff circuit cell; decode value
                value = tariffLine?.name;
                break;
              case SpecUnit.INTERVAL:
                value = formatTimestamp(parseInt(value as any));
                break;
              case SpecUnit.IO:
                {
                  // make sure value is an integer
                  const { isInput, bus, module, channel } = extractIOBusModuleChannel(
                    value as number,
                  );
                  const para = (isInput ? this.props.inputs : this.props.outputs) ?? [];
                  const io = (para as (IInput | IOutput)[]).find(
                    (curItem) =>
                      getBusNr(curItem.bus) === bus &&
                      curItem.module === module &&
                      curItem.channel === channel,
                  );
                  value = io?.name ?? value;
                }
                break;
            }
          } else {
            columnUnit = column.unit;
          }
        }
        // set the sum row unit; this needs to be done after processing to get the proper unit
        if (columnUnit && !sumRow[indexCol].unit) {
          sumRow[indexCol].unit = columnUnit;
        }

        if (isSet(column.decimals) && isSet(value) && typeof value === "number") {
          const factor =
            isSet(column.decimals) && column.decimals >= 0 ? Math.pow(10, column.decimals) : 1;
          value = parseFloat(value as any);
          value = factor * value;
          value = Math.round(value) / factor;
        }
        // otherwise leave value as string!
        const columnUnitText = isSet(columnUnit) ? " " + String(columnUnit) : "";
        aColumnArray.push(
          <td key={`dataCol_${indexCol}`} className={`${typeof value === "number" ? "right" : ""}`}>
            {isSet(value) ? String(value) + columnUnitText : ""}
          </td>,
        );
      });
      anArray.push(<tr key={`dataRow_${indexRow}`}>{aColumnArray}</tr>);
    });

    if (this.props.report?.hideSumRow !== true && sumRow.some((value) => isSet(value))) {
      anArray.push(this.renderSumRow(sumRow));
    }
    return anArray;
  }

  private renderSumRow(sumRow: ISumData[]) {
    // add sum-row
    return (
      <tr key={`dataRow_sum`} id="reportData_sum_row">
        {sumRow.map((aSumColumn, indexCol) => {
          const column = this.props.reportData?.colums?.[indexCol];
          let aValue: any = undefined;

          if (isSet(column) && column.aggregation !== undefined) {
            aValue =
              aSumColumn.avgCnt > 0 ? aSumColumn.value / aSumColumn.avgCnt : aSumColumn.value;

            if (isSet(column.decimals)) {
              const factor =
                isSet(column.decimals) && column.decimals >= 0 ? Math.pow(10, column.decimals) : 1;

              aValue = Math.round(factor * aValue) / factor;
            }

            // special value interpretation
            if (column.unit === SpecUnit.INTERVAL) {
              aValue = formatTimestamp(aValue);
            }
          }

          return (
            <td key={`dataCol_sum_${indexCol}`} className="right">
              <span>{`${isSet(aValue) ? String(aValue) : ""} ${
                isSet(aValue) ? aSumColumn.unit : ""
              }`}</span>
            </td>
          );
        })}
      </tr>
    );
  }

  public render() {
    return (
      <div id="reports" className="flex-self flex-content">
        <div className="headerbuttons mb-2">
          <div className="left">
            <div className="buttonbar d-print-none">
              <Button
                id="navigate-back"
                variant="secondary"
                onClick={() => this.props.navigate(-1)}
              >
                <FormattedMessage id="Back to report list" />
              </Button>
            </div>
          </div>
        </div>
        <div id="trendheading">
          <h1>
            <FormattedMessage id="Report" />: {this.props.report?.title}
          </h1>
        </div>
        {this.props.reportData?.data ?
          this.createTable()
        : this.props.reportError ?
          <div className={"alert alert-danger"}>
            <FormattedMessage id="Error while loading data" />: {this.props.reportError}
          </div>
        : this.props.reportData?.error !== undefined && this.props.reportData.error.length > 0 ?
          <div className={"alert alert-warning"}>
            <FormattedMessage id="Error while loading report" />: {this.props.reportData.error}
          </div>
        : <>
            <FormattedMessage id="Loading Data..." />
            <div id="loading-spinner" style={{ top: "10px" }}></div>
          </>
        }
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ReportDataViewComponent));
